import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Palette, Copy, Check } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";
import { useToast } from "@/hooks/use-toast";

const ColorPicker = () => {
  const { toast } = useToast();
  const [color, setColor] = useState("#3b82f6");
  const [copied, setCopied] = useState<string | null>(null);

  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null;
  };

  const rgbToHsl = (r: number, g: number, b: number) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }
    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
  };

  const rgb = hexToRgb(color);
  const hsl = rgb ? rgbToHsl(rgb.r, rgb.g, rgb.b) : null;

  const copy = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    toast({ title: `${label} copied!` });
    setTimeout(() => setCopied(null), 2000);
  };

  const relatedTools = getRelatedTools("color-picker");
  const formats = rgb && hsl ? [
    { label: "HEX", value: color.toUpperCase() },
    { label: "RGB", value: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` },
    { label: "HSL", value: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)` },
  ] : [];

  return (
    <ToolPageLayout
      title="Color Picker"
      description="Pick colors and convert between HEX, RGB, and HSL formats."
      metaDescription="Pick and convert colors between HEX, RGB, and HSL formats. Essential for design work and maintaining brand consistency."
      icon={Palette}
      slug="color-picker"
      formula={{ equation: "Color format conversions", variables: [{ name: "HEX", description: "#RRGGBB format" }, { name: "RGB", description: "Red, Green, Blue (0-255)" }, { name: "HSL", description: "Hue, Saturation, Lightness" }] }}
      example={{ inputs: [{ label: "HEX", value: "#3B82F6" }], output: "RGB(59, 130, 246) / HSL(217, 91%, 60%)", interpretation: "Convert between formats for different use cases." }}
      improvements={["Use HSL for easy color variations", "Ensure sufficient contrast for accessibility", "Create consistent color palettes"]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
    >
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Pick a Color</CardTitle>
          <CardDescription>Select or enter a color value</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex gap-4 items-end">
            <div className="space-y-2 flex-1">
              <Label>Color</Label>
              <div className="flex gap-2">
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-12 h-10 rounded cursor-pointer" />
                <Input value={color} onChange={(e) => setColor(e.target.value)} className="flex-1" />
              </div>
            </div>
          </div>
          <div className="w-full h-24 rounded-lg border" style={{ backgroundColor: color }} />
          <div className="space-y-2">
            {formats.map((f) => (
              <div key={f.label} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <span className="text-xs text-muted-foreground">{f.label}</span>
                  <p className="font-mono text-sm">{f.value}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => copy(f.value, f.label)}>
                  {copied === f.label ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </ToolPageLayout>
  );
};

export default ColorPicker;
